import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Flex,
	Input,
	InputNumber,
	Modal,
	Select,
	Space,
	Table,
	Tag,
	Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined, OpenAIOutlined } from '@ant-design/icons';
import {
	Balloon,
	ToleranceRow,
	ToleranceRowKind,
	ToleranceTable,
} from '../../interfaces/Ballooning';
import {
	countAffectedByTable,
	describeRow,
	ROW_KIND_LABELS,
	TABLE_SOURCE_LABELS,
} from '../../utils/toleranceTable';
import { viewportCappedModalStyles } from '../../utils/modalStyles';

interface ToleranceTableModalProps {
	open: boolean;
	table: ToleranceTable | null;
	suggestion: ToleranceTable | null;
	balloons: Balloon[];
	editable: boolean;
	onApply: (table: ToleranceTable | null, usedSuggestion: boolean) => void;
	onDismissSuggestion: () => void;
	onCancel: () => void;
}

// A row being typed may not have its tolerances yet; Apply is blocked until it does.
type DraftRow = Omit<ToleranceRow, 'plus' | 'minus'> & {
	plus: number | null;
	minus: number | null;
	key: string;
};

const KIND_OPTIONS = (Object.keys(ROW_KIND_LABELS) as ToleranceRowKind[]).map((kind) => ({
	value: kind,
	label: ROW_KIND_LABELS[kind],
}));

let keySeq = 0;
const withKeys = (rows: Omit<DraftRow, 'key'>[] = []): DraftRow[] =>
	rows.map((row) => ({ ...row, key: `row-${keySeq++}` }));

const stripKeys = (rows: DraftRow[]): ToleranceRow[] =>
	rows.map((row) => {
		const clean: Partial<DraftRow> = { ...row };
		delete clean.key;
		return clean as ToleranceRow;
	});

const isBlank = (v: unknown) => v === null || v === undefined || v === '';

const rowError = (row: DraftRow): string | null => {
	if (isBlank(row.plus) || isBlank(row.minus)) return 'Needs a plus and a minus.';
	if (row.kind === 'linear_range' && (isBlank(row.min) || isBlank(row.max)))
		return 'A size range needs a min and a max.';
	if (row.kind === 'linear_decimal' && isBlank(row.places))
		return 'Set the number of decimal places.';
	return null;
};

const ToleranceTableModal: React.FC<ToleranceTableModalProps> = ({
	open,
	table,
	suggestion,
	balloons,
	editable,
	onApply,
	onDismissSuggestion,
	onCancel,
}) => {
	const [rows, setRows] = useState<DraftRow[]>([]);
	const [notes, setNotes] = useState('');
	const [source, setSource] = useState<ToleranceTable['source']>('manual');
	const [usedSuggestion, setUsedSuggestion] = useState(false);

	useEffect(() => {
		if (!open) return;
		setRows(withKeys(table?.rows));
		setNotes(table?.notes ?? '');
		setSource(table?.source ?? 'manual');
		setUsedSuggestion(false);
	}, [open]);

	const errors = rows.map(rowError);
	const hasErrors = errors.some(Boolean);

	const draftTable = useMemo<ToleranceTable | null>(() => {
		const cleanRows = stripKeys(rows);
		if (!cleanRows.length && !notes.trim()) return null;
		return { source, rows: cleanRows, notes: notes.trim() };
	}, [rows, notes, source]);

	const affected = useMemo(
		() => (hasErrors ? 0 : countAffectedByTable(balloons, draftTable)),
		[balloons, draftTable, hasErrors]
	);

	const edit = (key: string, changes: Partial<DraftRow>) => {
		setSource('manual');
		setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...changes } : r)));
	};

	const addRow = () => {
		setSource('manual');
		setRows((prev) => [
			...prev,
			...withKeys([{ kind: 'linear_range', min: null, max: null, plus: null, minus: null }]),
		]);
	};

	const useSuggestion = () => {
		if (!suggestion) return;
		setRows(withKeys(suggestion.rows));
		setNotes(suggestion.notes || notes);
		setSource('ai');
		setUsedSuggestion(true);
	};

	const numberCell = (
		row: DraftRow,
		field: 'min' | 'max' | 'places' | 'plus' | 'minus',
		enabled: boolean,
		extra: Record<string, unknown> = {}
	) =>
		enabled ? (
			<InputNumber
				size="small"
				style={{ width: '100%' }}
				controls={false}
				value={row[field] as number | null}
				disabled={!editable}
				onChange={(value) => edit(row.key, { [field]: value ?? null })}
				{...extra}
			/>
		) : (
			<Typography.Text type="secondary">—</Typography.Text>
		);

	const columns = [
		{
			title: 'Applies to',
			width: 150,
			render: (row: DraftRow) => (
				<Select
					size="small"
					style={{ width: '100%' }}
					value={row.kind}
					options={KIND_OPTIONS}
					disabled={!editable}
					onChange={(kind: ToleranceRowKind) =>
						edit(row.key, {
							kind,
							min: kind === 'linear_range' || kind === 'angular' ? row.min : null,
							max: kind === 'linear_range' || kind === 'angular' ? row.max : null,
							places: kind === 'linear_decimal' || kind === 'angular' ? row.places : null,
						})
					}
				/>
			),
		},
		{
			title: 'Over',
			width: 80,
			render: (row: DraftRow) =>
				numberCell(row, 'min', row.kind === 'linear_range' || row.kind === 'angular'),
		},
		{
			title: 'Up to',
			width: 80,
			render: (row: DraftRow) =>
				numberCell(row, 'max', row.kind === 'linear_range' || row.kind === 'angular'),
		},
		{
			title: 'Decimals',
			width: 75,
			render: (row: DraftRow) =>
				numberCell(row, 'places', row.kind === 'linear_decimal' || row.kind === 'angular', {
					min: 0,
					max: 6,
					precision: 0,
				}),
		},
		{
			title: '−',
			width: 85,
			render: (row: DraftRow) => numberCell(row, 'minus', true, { min: 0 }),
		},
		{
			title: '+',
			width: 85,
			render: (row: DraftRow) => numberCell(row, 'plus', true, { min: 0 }),
		},
		{
			title: '',
			width: 36,
			render: (row: DraftRow) =>
				editable ? (
					<Button
						size="small"
						type="text"
						danger
						icon={<DeleteOutlined />}
						aria-label="Remove row"
						onClick={() => {
							setSource('manual');
							setRows((prev) => prev.filter((r) => r.key !== row.key));
						}}
					/>
				) : null,
		},
	];

	return (
		<Modal
			open={open}
			title={
				<Space>
					Tolerance table
					{table && <Tag>{TABLE_SOURCE_LABELS[source]}</Tag>}
				</Space>
			}
			width={760}
			centered
			styles={viewportCappedModalStyles}
			onCancel={onCancel}
			footer={
				editable
					? [
							<Button key="cancel" onClick={onCancel}>
								Cancel
							</Button>,
							<Button
								key="apply"
								type="primary"
								disabled={hasErrors}
								onClick={() => onApply(draftTable, usedSuggestion)}
								data-testid="tolerance-table-apply"
							>
								{affected > 0
									? `Apply (updates ${affected} balloon${affected === 1 ? '' : 's'})`
									: 'Apply'}
							</Button>,
						]
					: [
							<Button key="close" onClick={onCancel}>
								Close
							</Button>,
						]
			}
		>
			<Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
				General tolerances for dimensions printed without their own. A size range
				covers values over its first number up to and including its second. Lookup
				order: size range, then decimal places, then &ldquo;All linear&rdquo;. Balloons whose
				tolerance came from this table follow it; tolerances typed by hand or printed
				on the drawing are never changed. Thread depth rows are for reference only.
			</Typography.Paragraph>

			{suggestion && !usedSuggestion && (
				<Alert
					type="info"
					showIcon
					icon={<OpenAIOutlined />}
					style={{ marginBottom: 12 }}
					message="The drawing shows a different tolerance table"
					description={
						<>
							<ul style={{ margin: '4px 0 8px', paddingLeft: 18 }}>
								{suggestion.rows.map((row, i) => (
									<li key={i}>{describeRow(row)}</li>
								))}
							</ul>
							{editable && (
								<Space>
									<Button size="small" type="primary" onClick={useSuggestion}>
										Use this table
									</Button>
									<Button size="small" onClick={onDismissSuggestion}>
										Dismiss
									</Button>
								</Space>
							)}
						</>
					}
				/>
			)}

			<Table
				size="small"
				rowKey="key"
				tableLayout="fixed"
				scroll={{ x: 'max-content' }}
				pagination={false}
				dataSource={rows}
				columns={columns}
				locale={{ emptyText: 'No tolerance rows' }}
			/>
			{hasErrors && (
				<Typography.Text type="danger" style={{ display: 'block', marginTop: 6 }}>
					{errors.find(Boolean)}
				</Typography.Text>
			)}
			{editable && (
				<Button
					size="small"
					type="dashed"
					icon={<PlusOutlined />}
					onClick={addRow}
					style={{ marginTop: 8 }}
				>
					Add row
				</Button>
			)}
			<Flex vertical gap={4} style={{ marginTop: 12 }}>
				<Typography.Text strong>Notes</Typography.Text>
				<Input.TextArea
					autoSize={{ minRows: 2, maxRows: 5 }}
					value={notes}
					disabled={!editable}
					placeholder="Thread standards, finish, anything else from the tolerance block"
					onChange={(e) => {
						setSource('manual');
						setNotes(e.target.value);
					}}
				/>
			</Flex>
		</Modal>
	);
};

export default ToleranceTableModal;
